<template>
  <div class="markdown-content-preview">
    <div v-if="loading" class="markdown-content-preview__state">
      <i class="el-icon-loading state-icon"></i>
      <span>正在加载 Markdown 内容...</span>
    </div>
    <div v-else-if="error" class="markdown-content-preview__state">
      <i class="el-icon-warning-outline state-icon"></i>
      <span>{{ error }}</span>
    </div>
    <div v-else-if="htmlContent" class="markdown-content-preview__body">
      <div class="markdown-body" v-html="htmlContent"></div>
    </div>
    <div v-else class="markdown-content-preview__state">
      <span>暂无内容</span>
    </div>
  </div>
</template>

<script>
import MarkdownIt from 'markdown-it'
import axios from 'axios'
import { requestUrl } from '@/http/requestUrl'

export default {
  name: 'MarkdownContentPreview',
  props: {
    filePath: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      error: '',
      htmlContent: '',
      markdownRenderer: null,
      renderToken: 0
    }
  },
  watch: {
    filePath: {
      immediate: true,
      handler() {
        this.fetchMarkdownContent()
      }
    }
  },
  methods: {
    async fetchMarkdownContent() {
      if (!this.filePath) {
        this.htmlContent = ''
        return
      }
      const token = ++this.renderToken
      this.loading = true
      this.error = ''
      try {
        const fileUrl = await this.getMarkdownFileUrl(this.filePath)
        const res = await axios.get(fileUrl, { responseType: 'text' })
        if (token !== this.renderToken) {
          return
        }
        const markdownText = this.normalizeMarkdownContent(res?.data || '')
        let html = this.renderMarkdownContent(markdownText)
        html = await this.processImagesInHtml(html)
        if (token === this.renderToken) {
          this.htmlContent = html
        }
      } catch (err) {
        console.error('加载Markdown预览失败:', err)
        this.error = 'Markdown 内容加载失败，请稍后重试'
        if (token === this.renderToken) {
          this.htmlContent = ''
        }
      } finally {
        if (token === this.renderToken) {
          this.loading = false
        }
      }
    },

    normalizeMarkdownContent(content) {
      if (!content || typeof content !== 'string') {
        return ''
      }
      // 去掉代码块包裹
      // 首先处理 ```markdown 开头的代码块
      const markdownCodeBlockPattern = /^```\s*markdown\s*\n([\s\S]*?)\n```/m
      let match = content.match(markdownCodeBlockPattern)
      if (match && match[1]) {
        const afterCodeBlock = content.substring(match[0].length)
        return match[1] + afterCodeBlock
      }
      const markdownCodeBlockPattern2 = /^```\s*markdown\s*([\s\S]*?)\s*```/m
      match = content.match(markdownCodeBlockPattern2)
      if (match && match[1]) {
        const afterCodeBlock = content.substring(match[0].length)
        return match[1] + afterCodeBlock
      }
      
      // 处理没有语言标识的 ``` 代码块（如 ```\n# 标题\n```）
      const plainCodeBlockPattern = /^```\s*\n([\s\S]*?)\n```/m
      match = content.match(plainCodeBlockPattern)
      if (match && match[1]) {
        const afterCodeBlock = content.substring(match[0].length)
        return match[1] + afterCodeBlock
      }
      const plainCodeBlockPattern2 = /^```\s*([\s\S]*?)\s*```/m
      match = content.match(plainCodeBlockPattern2)
      if (match && match[1]) {
        const afterCodeBlock = content.substring(match[0].length)
        return match[1] + afterCodeBlock
      }
      
      return content
    },

    initMarkdownRenderer() {
      if (this.markdownRenderer) {
        return
      }
      this.markdownRenderer = new MarkdownIt({
        html: true,
        linkify: true,
        typographer: true,
        breaks: true
      })
    },

    renderMarkdownContent(content) {
      if (!content) return ''
      const text = typeof content === 'string' ? content : String(content)
      this.initMarkdownRenderer()
      try {
        return this.markdownRenderer.render(text)
      } catch (error) {
        console.error('renderMarkdownContent error:', error)
        return text
      }
    },

    async processImagesInHtml(htmlContent) {
      if (!htmlContent || typeof htmlContent !== 'string') {
        return htmlContent
      }

      const imgTagRegex = /<img[^>]*src\s*=\s*["']([^"']+)["'][^>]*>/gi
      const markdownImgRegex = /!\[([^\]]*)\]\(([^)]+)\)/g
      const imageUrls = new Set()
      let match

      while ((match = imgTagRegex.exec(htmlContent)) !== null) {
        const imgUrl = match[1]
        if (this.needSignImage(imgUrl)) {
          imageUrls.add(imgUrl)
        }
      }
      while ((match = markdownImgRegex.exec(htmlContent)) !== null) {
        const imgUrl = match[2]
        if (this.needSignImage(imgUrl)) {
          imageUrls.add(imgUrl)
        }
      }

      if (!imageUrls.size) {
        return htmlContent
      }

      const signedUrls = {}
      for (const imgUrl of imageUrls) {
        try {
          const previewApi = requestUrl.preview.previewUrl + '?isVersionManage=1&needEncrypt=false'
          const resp = await this.$http.post(previewApi, { fileInOssPath: imgUrl })
          if (resp.data && resp.data.code === '0') {
            signedUrls[imgUrl] = resp.data.data
          } else {
            console.error('图片签名失败:', resp.data)
          }
        } catch (err) {
          console.error('获取图片签名失败:', imgUrl, err)
        }
      }

      let processedHtml = htmlContent
      Object.keys(signedUrls).forEach(originalUrl => {
        const signedUrl = signedUrls[originalUrl]
        const escapedUrl = originalUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const replaceReg = new RegExp(escapedUrl, 'g')
        processedHtml = processedHtml.replace(replaceReg, signedUrl)
      })

      return processedHtml
    },

    needSignImage(url) {
      return (
        url &&
        url.includes('oss-cn-zhangjiakou.aliyuncs.com') &&
        !url.includes('Expires=')
      )
    },

    async getMarkdownFileUrl(ossPath) {
      if (!ossPath) {
        throw new Error('无效的文件路径')
      }
      if (ossPath.includes('Expires=')) {
        return ossPath
      }
      const previewApi = requestUrl.preview.previewUrl + '?isVersionManage=1&needEncrypt=false'
      const resp = await this.$http.post(previewApi, { fileInOssPath: ossPath })
      if (resp.data && resp.data.code === '0' && resp.data.data) {
        return resp.data.data
      }
      throw new Error(resp.data && resp.data.message ? resp.data.message : '获取Markdown地址失败')
    }
  }
}
</script>

<style lang="less" scoped>
.markdown-content-preview {
  width: 100%;
  min-height: 240px;
  background: #fff;
  border-radius: 6px;
  padding: 16px;
  box-sizing: border-box;
  overflow-y: auto;

  &__state {
    min-height: 160px;
    color: #666;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    .state-icon {
      font-size: 18px;
      color: #999;
    }
  }

  &__body {
    width: 100%;
  }

  .markdown-body {
    font-size: 14px;
    line-height: 1.8;
    color: #1f2d3d;

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      margin: 16px 0 8px;
      font-weight: 600;
    }

    p {
      margin: 8px 0;
      white-space: pre-wrap;
      word-break: break-word;
    }

    img {
      max-width: 100%;
      display: block;
      margin: 12px auto;
    }

    pre {
      background: #f6f8fa;
      padding: 12px;
      border-radius: 4px;
      overflow: auto;
    }

    code {
      background: #f6f8fa;
      padding: 2px 4px;
      border-radius: 4px;
      font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, Courier, monospace;
    }
  }
}
</style>
